// Durable outbox for outgoing messages.
//
// The SDK keeps unsent drafts in memory and only mirrors them to IndexedDB when the
// persistent cache is enabled ("keep me logged in"). With the cache off — the default —
// a page reload, a crash or a closed tab silently loses everything the user typed while
// offline. This module keeps a small, always-on localStorage copy of the pending sends so
// they can be replayed after a reload.
//
// Entries are removed as soon as the server acknowledges the message. A replay is
// at-least-once: if the {ctrl} response was lost in flight the message may be sent twice.
// That is a deliberate trade-off — a duplicate is far less damaging than a lost message.
//
// Messages with an in-flight upload are NOT persisted: the browser cannot serialize a
// File/Blob handle across a reload.

import { reportError } from './error-reporting.js';

const OUTBOX_KEY = 'apporigin.outbox';
// Give up on entries older than this so a permanently broken message cannot wedge
// the outbox forever.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
// Hard cap on the number of persisted entries.
const MAX_ENTRIES = 200;

function load() {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    if (!raw) {
      return [];
    }
    const val = JSON.parse(raw);
    return Array.isArray(val) ? val : [];
  } catch (err) {
    return [];
  }
}

function save(entries) {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(entries));
  } catch (err) {
    // Private mode / quota exceeded: degrade to in-memory-only behaviour.
    // This is the only signal we get that the durable outbox has stopped being durable,
    // i.e. that a reload will lose messages the user believes were sent. It has always
    // been swallowed silently. Only the error (a DOMException such as QuotaExceededError)
    // is reported -- never `entries`, which is the pending message text itself.
    reportError(err, {feature: 'outbox-persist', op: 'save'});
  }
}

let cache = null;

function entries() {
  if (!cache) {
    const cutoff = Date.now() - MAX_AGE_MS;
    cache = load().filter(e => e && e.topic && e.at > cutoff);
    if (cache.length > MAX_ENTRIES) {
      cache = cache.slice(cache.length - MAX_ENTRIES);
    }
  }
  return cache;
}

function flush() {
  save(cache || []);
}

let counter = 0;

// Unique-enough id for one pending send.
function makeId() {
  counter++;
  return `${Date.now().toString(36)}-${counter}-${Math.random().toString(36).slice(2, 8)}`;
}

// Stable-ish fingerprint of a pending message, used to avoid replaying a message which
// the SDK has already restored into its own in-memory send queue.
export function outboxSignature(content, head) {
  try {
    return JSON.stringify([content || null, head || null]);
  } catch (err) {
    return null;
  }
}

const Outbox = {
  // Persist a pending send. Returns the entry id, or null if the message cannot be
  // persisted (e.g. it carries an upload in progress).
  add(topicName, content, head, persistable) {
    if (!topicName || !persistable) {
      return null;
    }
    let serialized;
    try {
      // Round-trip through JSON right away: if the content is not serializable we want
      // to find out here rather than corrupting the whole outbox on save.
      serialized = JSON.parse(JSON.stringify({content: content, head: head || null}));
    } catch (err) {
      // The message is dropped from the durable outbox here and the caller has no way to
      // tell. Worth knowing about: it means some Drafty shape we produce is not
      // JSON-round-trippable. The error is a JSON/TypeError; `content` is never attached.
      reportError(err, {feature: 'outbox-persist', op: 'serialize'});
      return null;
    }
    const id = makeId();
    entries().push({
      id: id,
      topic: topicName,
      content: serialized.content,
      head: serialized.head,
      at: Date.now(),
      failed: false
    });
    if (cache.length > MAX_ENTRIES) {
      cache.splice(0, cache.length - MAX_ENTRIES);
    }
    flush();
    return id;
  },

  // The message was accepted by the server: forget about it.
  remove(id) {
    if (!id) {
      return;
    }
    const all = entries();
    const idx = all.findIndex(e => e.id == id);
    if (idx >= 0) {
      all.splice(idx, 1);
      flush();
    }
  },

  // The send failed; keep the entry but record it so the UI can surface it.
  markFailed(id, reason) {
    if (!id) {
      return;
    }
    const entry = entries().find(e => e.id == id);
    if (entry) {
      entry.failed = true;
      entry.reason = reason ? String(reason).slice(0, 200) : undefined;
      flush();
    }
  },

  // All pending entries for a topic, oldest first.
  forTopic(topicName) {
    return entries().filter(e => e.topic == topicName);
  },

  // Number of pending entries across all topics.
  count() {
    return entries().length;
  },

  clearTopic(topicName) {
    cache = entries().filter(e => e.topic != topicName);
    flush();
  }
};

export default Outbox;
